import React, { useState } from 'react';
import { Col, Input, Row } from 'antd';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons';

import Messages from 'i18n/messages/maintenance';
import {
  RibbonButton,
  StyledModal,
  AgGrid,
  FormButton,
  CustomNotification,
} from 'common/components';
import selectorsGridView from 'common/components/grid-view/controllers/selectors';
import { formatMDYWithParam } from 'utils/formatDate';

import * as api from 'config/axios';
import * as eulaMaintenanceEndpoints from 'services/eulaMaintenance/endpoints';
import { DownloadEulaAccepted } from 'services/eulaMaintenance';

const { Search } = Input;

const ViewAcceptance = () => {
  const [visible, setVisible] = useState(false);

  const detailCurrentItemsSelection = useSelector(
    selectorsGridView.makeSelectDetailCurrentITemsSelection()
  );

  const isDisabledBtn = detailCurrentItemsSelection?.length !== 1;

  return (
    <>
      <RibbonButton
        icon={<CheckCircleOutlined />}
        label={Messages.viewAcceptance}
        disabled={isDisabledBtn}
        onClick={() => setVisible(true)}
      />

      {visible && (
        <ViewAcceptanceModal
          visible={visible}
          onCancel={() => setVisible(false)}
          eulaId={detailCurrentItemsSelection?.[0]?.id}
        />
      )}
    </>
  );
};

const propertiesCol = {
  allowFilter: true,
  allowSort: true,
  resizable: true,
};

const defaultColumns = [
  {
    dataType: 'string',
    displayName: 'EULA Name',
    fieldName: 'EulaName',
    fieldNameCamelCase: 'eulaName',
    flex: 1,
    minWidth: 200,
    ...propertiesCol,
  },
  {
    dataType: 'string',
    displayName: 'Member Name',
    fieldName: 'MemberName',
    fieldNameCamelCase: 'memberName',
    minWidth: 200,
    ...propertiesCol,
  },
  {
    dataType: 'string',
    displayName: 'Status',
    fieldName: 'Status',
    fieldNameCamelCase: 'status',
    width: 100,
    ...propertiesCol,
  },
  {
    dataType: 'datetime',
    displayName: 'Created',
    fieldName: 'Created',
    fieldNameCamelCase: 'created',
    width: 100,
    cellRenderer: formatMDYWithParam,
    ...propertiesCol,
  },
  {
    dataType: 'datetime',
    displayName: 'EULA Accepted Date',
    fieldName: 'EulaAcceptedDate',
    fieldNameCamelCase: 'eulaAcceptedDate',
    cellRenderer: formatMDYWithParam,
    ...propertiesCol,
  },
  {
    dataType: 'string',
    displayName: 'User Email',
    fieldName: 'UserEmail',
    fieldNameCamelCase: 'userEmail',
    ...propertiesCol,
  },
  {
    dataType: 'string',
    displayName: 'User First Name',
    fieldName: 'UserFirstName',
    fieldNameCamelCase: 'userFirstName',
    width: 150,
    ...propertiesCol,
  },
  {
    dataType: 'string',
    displayName: 'User Last Name',
    fieldName: 'UserLastName',
    fieldNameCamelCase: 'userLastName',
    width: 150,
    ...propertiesCol,
  },
  {
    dataType: 'string',
    displayName: 'Eula Accepted Ip Address',
    fieldName: 'EulaAcceptedIpAddress',
    fieldNameCamelCase: 'eulaAcceptedIpAddress',
    width: 200,
    ...propertiesCol,
  },
  {
    dataType: 'string',
    displayName: 'Eula Accepted User Agent',
    fieldName: 'EulaAcceptedUserAgent',
    fieldNameCamelCase: 'eulaAcceptedUserAgent',
    width: 400,
    ...propertiesCol,
  },
];

const ViewAcceptanceModal = ({ visible, onCancel, eulaId }) => {
  const modalProps = {
    title: 'Accepted EULA',
    visible,
    onCancel,
    width: '60vw',
    bodyStyle: {
      height: '60vh',
    },
    maskClosable: false,
    footer: null,
  };

  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSearchItem = (searchTextValue) => {
    setSearchText(searchTextValue);
  };

  const handleDownloadEulaAccepted = () => {
    setLoading(true);
    DownloadEulaAccepted({
      eulaId,
    })
      .then((response) => {
        const { isSuccess, data, message } = response;

        if (isSuccess && data?.url) api.sendDownload({ url: data?.url });
        else CustomNotification.error(message || 'Server Error!');
      })
      .catch(() => CustomNotification.error('Server Error!'))
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <StyledModal {...modalProps}>
        <Row>
          <Col>
            <Search
              style={{ width: '346px', marginBottom: '20px' }}
              allowClear
              enterButton
              placeholder='Search Eula Acceptance...'
              onSearch={handleSearchItem}
            />
          </Col>
          <Col style={{ marginLeft: 'auto' }}>
            <FormButton
              text='Export'
              icon={<DownloadOutlined />}
              onClick={handleDownloadEulaAccepted}
              loading={loading}
            />
          </Col>
        </Row>
        <div
          id='my-grid-link-asset'
          className='ag-theme-alpine'
          style={{ height: '80%' }}
        >
          <AgGrid
            sortAndFilterServerside
            styleGrid={{ width: '100%', height: '100%' }}
            columnDefs={defaultColumns}
            requestParams={{ search: { searchText }, eulaId: eulaId }}
            responseParams='data'
            urlGrid={eulaMaintenanceEndpoints.GET_ACCEPTED_USER_EULA}
            urlGridDistinct={
              eulaMaintenanceEndpoints.ACCEPTED_USER_EULA_GRID_DISTINCT
            }
            notShowHeaderCheckbox={true}
            showCheckboxSelectionRender={false}
            gridConfigProps={{
              rowSelection: 'single',
            }}
            mapId={(item) => ({
              ...item,
              id: uuidv4(),
            })}
          />
        </div>
      </StyledModal>
    </>
  );
};

export default ViewAcceptance;
